import React, { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
  LinearProgress,
  Tooltip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme as useMuiTheme
} from '@mui/material';
import { useMultilingual } from '../hooks/useMultilingual';
import LanguageSelector from './LanguageSelector';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Category as CategoryIcon,
  DirectionsCar as CarIcon,
  Build as PartsIcon,
  ShoppingCart as OrdersIcon,
  People as UsersIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  Settings as SettingsIcon,
  AccountCircle as ProfileIcon,
  Logout as LogoutIcon,
  Campaign as CampaignIcon,
  ContactMail as ContactIcon,
  Public as PublicIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/auth';
import { useNavigation } from '../contexts/NavigationContext';

const drawerWidth = 268;
const SIDEBAR_STORAGE_KEY = 'edl-sidebar-open';

interface LayoutProps {
  children: React.ReactNode;
}

type NavItem = {
  text: string;
  textAr: string;
  icon: React.ReactNode;
  path: string;
  roles: string[];
};

type NavGroup = {
  id: string;
  label: string;
  labelAr: string;
  items: NavItem[];
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      return saved === null ? true : saved === 'true';
    } catch {
      return true;
    }
  });
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { isNavigating, startNavigation, stopNavigation } = useNavigation();
  const location = useLocation();
  const user = authService.getUser();
  const rolesKey = authService.getRoles().join('|').toLowerCase();
  const { getText, isRTL } = useMultilingual();
  const theme = useMuiTheme();

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarOpen));
    } catch {
      // ignore storage errors
    }
  }, [sidebarOpen]);

  const navGroups: NavGroup[] = useMemo(
    () => [
      {
        id: 'overview',
        label: 'Overview',
        labelAr: 'نظرة عامة',
        items: [
          { text: 'Dashboard', textAr: 'لوحة التحكم', icon: <DashboardIcon />, path: '/dashboard', roles: ['Admin', 'Manager', 'Customer'] }
        ]
      },
      {
        id: 'catalog',
        label: 'Catalog',
        labelAr: 'الكتالوج',
        items: [
          { text: 'Parts', textAr: 'القطع', icon: <PartsIcon />, path: '/parts', roles: ['Admin', 'Manager'] },
          { text: 'Categories', textAr: 'الفئات', icon: <CategoryIcon />, path: '/categories', roles: ['Admin', 'Manager'] },
          { text: 'Brands', textAr: 'العلامات', icon: <BusinessIcon />, path: '/brands', roles: ['Admin', 'Manager'] },
          { text: 'Countries', textAr: 'دول المنشأ', icon: <PublicIcon />, path: '/countries-made', roles: ['Admin', 'Manager'] },
          { text: 'Vehicles', textAr: 'المركبات', icon: <CarIcon />, path: '/vehicles', roles: ['Admin', 'Manager'] }
        ]
      },
      {
        id: 'commerce',
        label: 'Commerce',
        labelAr: 'التجارة',
        items: [
          { text: 'Orders', textAr: 'الطلبات', icon: <OrdersIcon />, path: '/orders', roles: ['Admin', 'Manager'] },
          { text: 'Payments', textAr: 'المدفوعات', icon: <PaymentIcon />, path: '/payments', roles: ['Admin', 'Manager'] },
          { text: 'Shipping', textAr: 'الشحن', icon: <ShippingIcon />, path: '/shipping', roles: ['Admin', 'Manager'] },
          { text: 'Advertisements', textAr: 'الإعلانات', icon: <CampaignIcon />, path: '/advertisements', roles: ['Admin', 'Manager'] }
        ]
      },
      {
        id: 'system',
        label: 'System',
        labelAr: 'النظام',
        items: [
          { text: 'Users', textAr: 'المستخدمون', icon: <UsersIcon />, path: '/users', roles: ['Admin'] },
          { text: 'Contact', textAr: 'التواصل', icon: <ContactIcon />, path: '/contact-info', roles: ['Admin'] },
          { text: 'Settings', textAr: 'الإعدادات', icon: <SettingsIcon />, path: '/settings', roles: ['Admin'] }
        ]
      }
    ],
    []
  );

  const flatItems = useMemo(() => navGroups.flatMap((g) => g.items), [navGroups]);

  const filteredGroups = useMemo(
    () =>
      navGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => authService.hasAnyRole(item.roles))
        }))
        .filter((group) => group.items.length > 0),
    // Recompute when login roles change (Manager vs Admin vs empty).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navGroups, rolesKey]
  );

  const activeGroupId = useMemo(() => {
    const match = filteredGroups.find((group) =>
      group.items.some(
        (item) =>
          location.pathname === item.path
          || (item.path !== '/dashboard' && location.pathname.startsWith(item.path))
      )
    );
    return match?.id ?? filteredGroups[0]?.id ?? 'overview';
  }, [filteredGroups, location.pathname]);

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Expand every visible group so Manager sees Catalog/Commerce without hunting accordions.
  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = { ...prev };
      filteredGroups.forEach((group) => {
        next[group.id] = true;
      });
      return next;
    });
  }, [filteredGroups, activeGroupId]);

  const currentItem = flatItems.find((item) => location.pathname === item.path)
    || flatItems.find((item) => item.path !== '/dashboard' && location.pathname.startsWith(item.path));

  const handleAccordionChange = (groupId: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: isExpanded }));
  };

  const handleMobileDrawerToggle = () => setMobileOpen((open) => !open);

  const handleSidebarToggle = () => setSidebarOpen((open) => !open);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => setUserMenuAnchor(null);

  const handleLogout = async () => {
    handleUserMenuClose();
    await authService.logout();
    navigate('/login');
  };

  const handleNavigation = (path: string) => {
    if (location.pathname === path) {
      setMobileOpen(false);
      return;
    }
    startNavigation();
    navigate(path);
    setMobileOpen(false);
    window.setTimeout(() => stopNavigation(), 280);
  };

  const desktopDrawerWidth = sidebarOpen ? drawerWidth : 0;
  const HideSidebarIcon = isRTL ? ChevronRightIcon : ChevronLeftIcon;
  const ShowSidebarIcon = isRTL ? ChevronLeftIcon : ChevronRightIcon;

  const brandBlock = (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 1.5,
          bgcolor: '#1a1a1a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box
          component="img"
          src="/logo192.png"
          alt="EDL"
          sx={{ width: 34, height: 34, objectFit: 'contain' }}
        />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle1" fontWeight={800} noWrap sx={{ lineHeight: 1.2 }}>
          EDL Auto Parts
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {getText('Admin console', 'لوحة الإدارة')}
        </Typography>
      </Box>
      <Tooltip title={getText('Hide sidebar', 'إخفاء الشريط الجانبي')}>
        <IconButton
          size="small"
          onClick={handleSidebarToggle}
          sx={{ display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0 }}
          aria-label="hide sidebar"
        >
          <HideSidebarIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const navList = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {brandBlock}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1, px: 1 }}>
        {filteredGroups.map((group) => {
          const expanded = expandedGroups[group.id] ?? group.id === activeGroupId;
          const groupHasActive = group.items.some(
            (item) =>
              location.pathname === item.path
              || (item.path !== '/dashboard' && location.pathname.startsWith(item.path))
          );

          return (
            <Accordion
              key={group.id}
              disableGutters
              elevation={0}
              expanded={expanded}
              onChange={handleAccordionChange(group.id)}
              sx={{
                bgcolor: 'transparent',
                '&:before': { display: 'none' },
                mb: 0.5,
                borderRadius: 1.5,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: groupHasActive ? 'rgba(255, 101, 0, 0.35)' : 'transparent',
                '&.Mui-expanded': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
                sx={{
                  minHeight: 44,
                  px: 1.5,
                  '& .MuiAccordionSummary-content': {
                    my: 0.75,
                    alignItems: 'center',
                    gap: 1
                  },
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: 0.7,
                    textTransform: 'uppercase',
                    color: groupHasActive ? 'primary.main' : 'text.secondary',
                    fontSize: '0.72rem'
                  }}
                >
                  {getText(group.label, group.labelAr)}
                </Typography>
                <Chip
                  size="small"
                  label={group.items.length}
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    bgcolor: groupHasActive ? 'rgba(255, 101, 0, 0.12)' : 'action.hover',
                    color: groupHasActive ? 'primary.main' : 'text.secondary'
                  }}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, pb: 1 }}>
                <List dense disablePadding>
                  {group.items.map((item) => {
                    const selected =
                      location.pathname === item.path
                      || (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                    return (
                      <ListItemButton
                        key={item.path}
                        selected={selected}
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          mx: 1,
                          mb: 0.35,
                          borderRadius: 1.5,
                          py: 0.9,
                          transition: 'background-color 0.15s, color 0.15s',
                          '&.Mui-selected': {
                            bgcolor: 'rgba(255, 101, 0, 0.12)',
                            color: 'primary.main',
                            '& .MuiListItemIcon-root': { color: 'primary.main' },
                            '&:hover': { bgcolor: 'rgba(255, 101, 0, 0.18)' },
                            ...(isRTL
                              ? { borderRight: '3px solid', borderRightColor: 'primary.main', pr: 1.5 }
                              : { borderLeft: '3px solid', borderLeftColor: 'primary.main', pl: 1.5 })
                          },
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: selected ? 'primary.main' : 'text.secondary' }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={getText(item.text, item.textAr)}
                          primaryTypographyProps={{
                            fontWeight: selected ? 700 : 500,
                            fontSize: '0.9rem',
                            noWrap: true
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: { xs: 'block', sm: 'none' } }}>
        <LanguageSelector />
      </Box>
    </Box>
  );

  const primaryRole = authService.getRoles()[0] || user?.roles?.[0] || 'User';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <CssBaseline />

      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          width: {
            xs: '100%',
            sm: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%'
          },
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : '#fff',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
          }),
          ...(isRTL
            ? { mr: { sm: sidebarOpen ? `${drawerWidth}px` : 0 } }
            : { ml: { sm: sidebarOpen ? `${drawerWidth}px` : 0 } })
        }}
      >
        {isNavigating && (
          <LinearProgress
            color="primary"
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }}
          />
        )}
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, gap: 1 }}>
          {/* Mobile: open temporary drawer */}
          <IconButton
            aria-label="open navigation"
            edge={isRTL ? 'end' : 'start'}
            onClick={handleMobileDrawerToggle}
            sx={{
              display: { xs: 'inline-flex', sm: 'none' },
              ...(isRTL ? { ml: 0.5 } : { mr: 0.5 })
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Desktop: show/hide permanent sidebar */}
          <Tooltip
            title={
              sidebarOpen
                ? getText('Hide sidebar', 'إخفاء الشريط الجانبي')
                : getText('Show sidebar', 'إظهار الشريط الجانبي')
            }
          >
            <IconButton
              aria-label={sidebarOpen ? 'hide sidebar' : 'show sidebar'}
              edge={isRTL ? 'end' : 'start'}
              onClick={handleSidebarToggle}
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                ...(isRTL ? { ml: 0.5 } : { mr: 0.5 }),
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5
              }}
            >
              {sidebarOpen ? <HideSidebarIcon /> : <ShowSidebarIcon />}
            </IconButton>
          </Tooltip>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
              {getText(
                currentItem?.text || 'Dashboard',
                currentItem?.textAr || 'لوحة التحكم'
              )}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              {getText('Manage catalog, orders, and store settings', 'إدارة الكتالوج والطلبات وإعدادات المتجر')}
            </Typography>
          </Box>

          {user && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <LanguageSelector />
              </Box>

              <Chip
                size="small"
                label={primaryRole}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  fontWeight: 600,
                  bgcolor: 'rgba(255, 101, 0, 0.1)',
                  color: 'primary.main',
                  border: '1px solid',
                  borderColor: 'rgba(255, 101, 0, 0.25)'
                }}
              />

              <Tooltip title={getText('Account', 'الحساب')}>
                <IconButton onClick={handleUserMenuOpen} size="small" sx={{ p: 0.5 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: 'primary.main',
                      fontWeight: 700,
                      fontSize: '0.95rem'
                    }}
                  >
                    {(user.firstName?.charAt(0) || user.email?.charAt(0) || 'U').toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { sm: desktopDrawerWidth },
          flexShrink: { sm: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
          })
        }}
        aria-label="sidebar"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleMobileDrawerToggle}
          ModalProps={{ keepMounted: true }}
          anchor={isRTL ? 'right' : 'left'}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 0
            }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end', p: 1 }}>
            <IconButton onClick={handleMobileDrawerToggle} aria-label="close navigation">
              <CloseIcon />
            </IconButton>
          </Box>
          {navList}
        </Drawer>

        <Drawer
          variant="persistent"
          open={sidebarOpen}
          anchor={isRTL ? 'right' : 'left'}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderColor: 'divider',
              bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : '#fafafa',
              transition: theme.transitions.create('transform', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen
              }),
              ...(isRTL
                ? { right: 0, left: 'auto', borderLeft: '1px solid', borderRight: 0 }
                : {})
            }
          }}
        >
          {navList}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: {
            xs: '100%',
            sm: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%'
          },
          mt: { xs: 7, sm: 8 },
          minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
          bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#f5f5f5',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
          })
        }}
      >
        {children}
      </Box>

      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRTL ? 'left' : 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: isRTL ? 'left' : 'right' }}
        PaperProps={{
          elevation: 3,
          sx: { mt: 1, minWidth: 220, borderRadius: 2, border: '1px solid', borderColor: 'divider' }
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || getText('User', 'مستخدم')}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            handleUserMenuClose();
            handleNavigation('/profile');
          }}
        >
          <ProfileIcon sx={{ ...(isRTL ? { ml: 1.5 } : { mr: 1.5 }), fontSize: 20 }} />
          {getText('Profile', 'الملف الشخصي')}
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <LogoutIcon sx={{ ...(isRTL ? { ml: 1.5 } : { mr: 1.5 }), fontSize: 20 }} />
          {getText('Logout', 'تسجيل الخروج')}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;
